import encountersEnum from '@/constants/encounters.enum.js';

/**
 * Um registro clinico so deixa de ser rascunho quando o servidor o assina
 * (`finalizedAt`). A compatibilidade por `status` vale apenas para a consulta,
 * finalizada antes de `finalizedAt` existir: em vacina e exame o `status` tem
 * outro significado (no exame, 1 e "Em andamento") e diria o contrario do chip
 * mostrado no card, que le so o `finalizedAt`.
 * @param {object} item
 * @param {number|null} finishedStatus status legado que significa finalizado
 * @returns {boolean}
 */
const isDraftRecord = (item, finishedStatus = null) => {
    if (!item) return true;
    if (item.finalizedAt) return false;
    return !(finishedStatus !== null && item.status === finishedStatus);
};

/**
 * A internacao termina com a alta (finalizacao). Registros criados antes da
 * finalizacao explicita guardavam o desfecho no `status` (1 alta, 2 obito,
 * 3 transferencia), entao qualquer status maior que zero tambem encerra.
 */
const isClosedHospitalization = item => !!(item && (item.finalizedAt || item.status >= 1));

export default {
    encounters: state => state.encounters,
    encountersCursor: state => state.encountersCursor,
    hasMoreEncounters: state => state.hasMoreEncounters,
    loadingEncounters: state => state.loadingEncounters,
    selectedEncounter: state => state.selectedEncounter,
    anamnesis: state => state.anamnesis,
    physicalExam: state => state.physicalExam,
    diagnoses: state => state.diagnoses,
    vaccinations: state => state.vaccinations,
    vaccinationSchedule: state => state.vaccinationSchedule,
    loadingVaccinations: state => state.loadingVaccinations,
    labExams: state => state.labExams,
    loadingLabExams: state => state.loadingLabExams,
    hospitalizations: state => state.hospitalizations,
    loadingHospitalizations: state => state.loadingHospitalizations,
    prescriptions: state => state.prescriptions,
    loadingPrescriptions: state => state.loadingPrescriptions,
    pagination: state => state.pagination,
    hasMoreTimeline: state => Object.keys(state.pagination || {}).some(type => state.pagination[type].hasMore),
    loadingMoreTimeline: state => Object.keys(state.pagination || {}).some(type => state.pagination[type].loading),
    timelinePaginationErrors: state => Object.keys(state.pagination || {}).reduce((errors, type) => {
        if (state.pagination[type].error) errors[type] = state.pagination[type].error;
        return errors;
    }, {}),
    truncated: state => state.truncated,
    /**
     * Internacao em curso: existe enquanto a alta (finalizacao) nao acontece.
     */
    activeHospitalization: state => {
        return (state.hospitalizations || []).find(h => !isClosedHospitalization(h) && !h.deleted);
    },
    timelineFilter: state => state.timelineFilter,
    hideDrafts: state => state.hideDrafts,
    timeline: state => {
        const encounters = (state.encounters || []).map(e => ({
            ...e,
            _type: 'encounter',
            _date: e.date || e.lastUpdate,
            _draft: isDraftRecord(e, encountersEnum.STATUS.FINISHED)
        }));
        const vaccinations = (state.vaccinations || []).map(v => ({
            ...v,
            _type: 'vaccination',
            _date: v.applicationDate || v.lastUpdate,
            _draft: isDraftRecord(v)
        }));
        const labExams = (state.labExams || []).map(l => ({
            ...l,
            _type: 'lab_exam',
            _date: l.requestDate || l.lastUpdate,
            _draft: isDraftRecord(l)
        }));
        const hospitalizations = (state.hospitalizations || []).map(h => ({
            ...h,
            _type: 'hospitalization',
            _date: h.admissionDate || h.lastUpdate,
            _draft: !isClosedHospitalization(h)
        }));
        // A prescricao nao tem data clinica propria: `shareDate` e texto formatado
        // para exibicao, entao a ordenacao usa sempre o `lastUpdate` numerico.
        const prescriptions = (state.prescriptions || []).map(p => ({
            ...p,
            _type: 'prescription',
            _date: p.lastUpdate,
            _draft: !p.shareDate
        }));
        return [
            ...encounters,
            ...vaccinations,
            ...labExams,
            ...hospitalizations,
            ...prescriptions
        ].sort((a, b) => {
            const diff = (b._date || 0) - (a._date || 0);
            // Desempate estavel: sem ele itens com a mesma data trocam de lugar a
            // cada recalculo do getter e a lista "pisca" para o usuario.
            if (diff !== 0) return diff;
            return String(a.id || '').localeCompare(String(b.id || ''));
        });
    },
    filteredTimeline: (state, getters) => {
        const filter = state.timelineFilter;
        let items = getters.timeline;
        if (filter === 'encounters') items = items.filter(i => i._type === 'encounter');
        else if (filter === 'vaccinations') items = items.filter(i => i._type === 'vaccination');
        else if (filter === 'lab_exams') items = items.filter(i => i._type === 'lab_exam');
        else if (filter === 'hospitalizations') items = items.filter(i => i._type === 'hospitalization');
        else if (filter === 'prescriptions') items = items.filter(i => i._type === 'prescription');
        if (state.hideDrafts) {
            items = items.filter(i => !i._draft);
        }
        return items;
    }
}
